import {BehaviorSubject, Observable, of} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {MemberDirectEnvService} from '@clients/member-direct/survey-common';
import {SurveysCombinedReducerState, SurveysFacade} from '@clients/member-direct/surveys';
import {ENV_SERVICE} from '@clients/shared/config';
import {Logger, LogSeverity} from '@clients/shared/logging';
import {Store} from '@ngrx/store';

import {ClientStateSaveFailed} from '../+state/app/app.actions';
import {BlocksFacade} from 'libs/member-direct/surveys/src/lib/+state/blocks/blocks.facade';
import {MEMBER_DIRECT_ERRORS} from '../errors/member-direct-errors';
import {SaveClientStatePayload} from '../models/SaveClientStatePayload.interface';

export const stateServiceEndpoints = (api: string) => ({
    save: (context: string) => `${api}/interaction/code/${context}/records`,
    retrieve: (surveyRecordId: string) => `${api}/surveys/${surveyRecordId}/clientState`
});

interface ClientStateData {
    surveyRecordId: string;
    state: SaveClientStatePayload;
}

@Injectable()
export class StateService {
    private clientState: BehaviorSubject<ClientStateData> = new BehaviorSubject<ClientStateData>(null);

    private api: string;
    private isDevMode: boolean;
    constructor(
        @Inject(ENV_SERVICE) envService: MemberDirectEnvService,
        private http: HttpClient,
        private surveysFacade: SurveysFacade,
        private blocksFacade: BlocksFacade,
        private logger: Logger,
        private store: Store<any>
    ) {
        this.isDevMode = envService.env === "dev" || envService.env === "local";
        (this.api = envService.surveyServiceApi), this.subscription();
    }
    public save(surveyRecordId: string, state: SaveClientStatePayload) {
        this.clientState.next({
            surveyRecordId,
            state
        });
    }

    public retrieve(surveyRecordId: string): Observable<Object> {
        return this.http.get(stateServiceEndpoints(this.api).retrieve(surveyRecordId));
    }

    public assemblePayload(): SaveClientStatePayload {
        const state: SurveysCombinedReducerState = this.surveysFacade.getSurveysFeatureState();
        const blockIsLast = this.blocksFacade.blockIsLast(state.blocks.idOfSelectedEntity);
        const responsesCompleted = this.isResponsesCompleted(blockIsLast, state.blocks.idOfSelectedEntity);

        // exclude languageCode in Entities obj
        const responsesEntities = Object.entries(state.responses.entities).reduce((newEntity, [key, { languageCode, ...rest }]) => {
            newEntity[key] = rest;
            return newEntity;
            }, {});

        return {
            surveys: {
                blocks: {
                    idOfSelectedEntity: state.blocks.idOfSelectedEntity,
                    idOfPreviouslySelectedEntity: state.blocks.idOfPreviouslySelectedEntity,
                    hidden: state.blocks.hidden,
                    seen: state.blocks.seen,
                    completed: state.blocks.completed
                },
                elements: {
                    idOfSelectedEntity: state.elements.idOfSelectedEntity,
                    hidden: state.elements.hidden
                },
                responses: {
                    ids: state.responses.ids,
                    entities: responsesEntities,
                    complete: state.responses.complete || responsesCompleted,
                }
            }
        };
    }
    /* istanbul ignore next */
    private subscription() {
        this.clientState.pipe(debounceTime(200)).subscribe((data: ClientStateData) => {
            if (!data) return;

            this.saveClientState(data).subscribe(
                () => {},
                (err) => {
                    this.store.dispatch(new ClientStateSaveFailed(err));
                    this.logger.log(
                        'could not save client state',
                        {err, data},
                        LogSeverity.ERROR,
                        MEMBER_DIRECT_ERRORS.SAVE_CLIENT_STATE_FAILED
                    );
                }
            );
        });
    }

    private saveClientState(data: ClientStateData): Observable<Object> {
        const state = this.surveysFacade.getSurveysFeatureState();
        const endpoint: string = stateServiceEndpoints(this.api).save(state.survey.context);
        if (this.isDevMode) {
            console.log('SaveClientState: ', data);
        }
        return this.http.post(endpoint, data.state);
    }

    private isResponsesCompleted(blockIsLast: boolean, idOfSelectedEntity: string): boolean {
        const blocksCompleted: string[] = this.blocksFacade.completed();
        const blockIds: string[] = this.blocksFacade.getIds().map(id => String(id));
        // Check if a block is completed or not
        const isCompleted = id => blocksCompleted.includes(id);
        // filter the last block
        const filteredIds = blockIsLast ? blockIds.filter(id => id !== idOfSelectedEntity) : blockIds;
        // Check if all block is completed or not
        const allCompleted = filteredIds.every(isCompleted);
        return allCompleted;
    };
}
